import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { AddButton, Empty, ErrorNote, Pager, pageSlice } from "../../components/ui";

export interface CustomerRow {
  id: string;
  first_name: string; middle_name: string | null; last_name: string;
  city: string; county: string | null; state: string; zip: string;
  phone_cell: string | null; phone_home: string | null; email: string | null;
  created_at: string;
}

export const customerName = (c: {
  first_name: string; middle_name?: string | null; last_name: string;
}) => [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(" ");

export const prettyPhone = (d: string | null) =>
  d && d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : "";

type SortKey = "name" | "city" | "county" | "added";

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });

export default function CustomerList({ onAdd, onOpen }: {
  onAdd: () => void; onOpen: (id: string) => void;
}) {
  const [rows, setRows] = useState<CustomerRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [county, setCounty] = useState("");
  const [sort, setSort] = useState<SortKey>("name");
  const [desc, setDesc] = useState(false);

  useEffect(() => {
    supabase.from("customers")
      .select(`id,first_name,middle_name,last_name,city,county,state,zip,
               phone_cell,phone_home,email,created_at`)
      .order("last_name")
      .then(({ data, error }) => {
        if (error) setErr("Could not load customers.");
        else setRows((data ?? []) as CustomerRow[]);
      });
  }, []);

  const counties = useMemo(
    () => Array.from(new Set((rows ?? []).map((c) => c.county).filter(Boolean))).sort() as string[],
    [rows]);

  const sortName = (c: CustomerRow) => `${c.last_name}, ${c.first_name}`;

  const [page, setPage] = useState(0);
  /* Any change to the filters or the ordering starts again at the top:
     staying on page 3 of a result that now has one page shows nothing. */
  useEffect(() => { setPage(0); }, [q, county]);

  const shown = useMemo(() => {
    if (!rows) return [];
    const n = q.trim().toLowerCase();
    const digits = n.replace(/\D/g, "");
    let out = rows.filter((c) => {
      if (county && c.county !== county) return false;
      if (!n) return true;
      return customerName(c).toLowerCase().includes(n)
        || (c.city ?? "").toLowerCase().includes(n)
        || (c.county ?? "").toLowerCase().includes(n)
        || (c.email ?? "").toLowerCase().includes(n)
        || (c.zip ?? "").includes(n)
        || (digits.length >= 3 &&
            ((c.phone_cell ?? "").includes(digits) || (c.phone_home ?? "").includes(digits)));
    });

    const val = (c: CustomerRow) => ({
      name: sortName(c), city: c.city, county: c.county ?? "", added: c.created_at,
    })[sort];

    return [...out].sort((a, b) => {
      const r = String(val(a)).localeCompare(String(val(b)));
      return desc ? -r : r;
    });
  }, [rows, q, county, sort, desc]);


  /* Only this page is rendered; `shown` stays the full filtered set so the
     counts and the pager describe everything that matched. */
  const paged = pageSlice(shown, page);
  if (err) return <ErrorNote>{err}</ErrorNote>;
  if (!rows) return <p className="hint">Loading</p>;

  const th = (key: SortKey, label: string) => (
    <th className={sort === key ? "sorted" : ""}
        onClick={() => { sort === key ? setDesc(!desc) : (setSort(key), setDesc(false)); }}>
      {label}{sort === key && <span className="arrow">{desc ? "▾" : "▴"}</span>}
    </th>
  );

  return (
    <>
      <div className="pagehead">
        <div>
          <h2>Customers</h2>
          {/* A bare number under a heading reads as a stray digit. Every
              other list says what it is counting. */}
          <p className="pagehead-sub">
            {rows.length === 1 ? "1 customer" : `${rows.length} customers`}
          </p>
        </div>
        <AddButton label="Add customer" onClick={onAdd} />
      </div>

      {rows.length === 0 ? (
        <Empty>No customers yet.<br />Add one before you write a deal.</Empty>
      ) : (
        <>
          <div className="filters">
            <input className="search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search name, phone, city, ZIP or email" />
            {counties.length > 1 && (
              <select value={county} onChange={(e) => setCounty(e.target.value)}>
                <option value="">All counties</option>
                {counties.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>

          {(q || county) && (
            <p className="resultnote">
              {shown.length} of {rows.length} —{" "}
              <button className="linkbtn" onClick={() => { setQ(""); setCounty(""); }}>
                clear
              </button>
            </p>
          )}

          {shown.length === 0 ? <Empty>Nobody matches that.</Empty> : (
            <>
              <div className="tablewrap">
                <table className="dealtable">
                  <thead>
                    <tr>
                      {th("name", "Name")}
                      <th>Phone</th>
                      <th>Email</th>
                      {th("city", "City")}
                      {th("county", "County")}
                      <th>ZIP</th>
                      {th("added", "Added")}
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((c) => (
                      <tr key={c.id} onClick={() => onOpen(c.id)}>
                        <td className="strong">{sortName(c)}</td>
                        <td>{prettyPhone(c.phone_cell) || prettyPhone(c.phone_home) || "—"}</td>
                        <td className="ellip">{c.email ?? "—"}</td>
                        <td>{c.city}</td>
                        <td>{c.county ?? <span className="dim">—</span>}</td>
                        <td className="mono dim">{c.zip}</td>
                        <td className="dim nowrap">{shortDate(c.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="rows dealcards">
                {paged.map((c) => (
                  <button key={c.id} className="row" onClick={() => onOpen(c.id)}>
                    <div className="row-top"><span className="desc">{customerName(c)}</span></div>
                    <div className="row-bot">
                      <span className="meta">
                        <span>{c.city}{c.county ? `, ${c.county}` : ""}</span>
                      </span>
                      <span className="meta">
                        {prettyPhone(c.phone_cell) || prettyPhone(c.phone_home) || c.email}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              <Pager page={page} setPage={setPage} total={shown.length} />
            </>
          )}
        </>
      )}
    </>
  );
}
